import config from 'shared/config';
import connector from 'api/http';
import { ApiData, ApiErrorReasonType } from 'scenes/common/types';
import { makeApiError, makeBusinessError } from 'scenes/common/helpers/apiData';
import { MotorQuoteDetails } from 'scenes/motor/types';
import { HomeQuoteDetails } from 'scenes/home/types';
import { LandlordQuoteDetails } from 'scenes/landlord/types';
import { unmarshalQuoteResponse } from 'scenes/motor/api/quote/marshaller/marshaller';
import { unmarshalQuoteResponse as unmarshalHomeQuoteResponse } from 'scenes/home/api/quote/marshaller';
import { unmarshalQuoteResponse as unmarshalLandlordQuoteResponse } from 'scenes/landlord/api/quote/marshaller';
import {
  BRAND,
  REQUEST_VERSION,
  WireChannel,
  WirePortfolio,
  WireProduct,
  WireStatus,
} from 'scenes/common/api/quote/types';
import { toQuoteNumberWithDashes } from 'scenes/common/helpers/quote';
import { toYYYYMMDD } from 'scenes/common/helpers/datetime';
import { WireHomeQuoteResponse } from 'scenes/home/api/quote/types';

// quotes retrieved which were originally created in claims centre should temporarily NOT be processed
const CLAIMS_CENTRE_CHANNELS = ['CC', 'WebCC'];

export const retrieveMotorQuote = (
  quoteNumber: string,
  postcode: string,
  dob: string
): Promise<ApiData<MotorQuoteDetails>> => {
  const server = connector(config.baseUrls.BFF_BASE_URL);

  const request = {
    brand: BRAND,
    product: WireProduct.Motor,
    portfolio: WirePortfolio.ColesCp,
    channel: WireChannel.Web,
    version: REQUEST_VERSION,
    targetStatus: WireStatus.RetrieveQuote,
    policyNumber: toQuoteNumberWithDashes(quoteNumber),
    dob: toYYYYMMDD(dob),
    postcode,
  };

  return Promise.resolve()
    .then(() => server.post('/quote/motor/retrieve', request))
    .then(unmarshalQuoteResponse) // TODO: Create an unmarshalQuoteResponse for Retrieve, instead of using the Motor one
    .catch(() =>
      makeApiError(
        'Unable to retrieve your quote based on the details provided. Please try again or return to the Coles Insurance home page to do another quote.'
      )
    );
};

export const retrieveHomeQuote = (
  quoteNumber: string,
  postcode: string,
  dob: string
): Promise<ApiData<HomeQuoteDetails>> => {
  const server = connector(config.baseUrls.BFF_BASE_URL);

  const request = {
    brand: BRAND,
    product: WireProduct.Home,
    portfolio: WirePortfolio.ColesCp,
    channel: WireChannel.Web,
    version: REQUEST_VERSION,
    targetStatus: WireStatus.RetrieveQuote,
    policyNumber: toQuoteNumberWithDashes(quoteNumber),
    dob: toYYYYMMDD(dob),
    postcode,
  };

  return Promise.resolve()
    .then(() => server.post('/quote/home/retrieve', request))
    .then((response: WireHomeQuoteResponse) => {
      const {
        payload: { channel },
      } = response;
      if (channel && CLAIMS_CENTRE_CHANNELS.includes(channel)) {
        return makeBusinessError(
          ApiErrorReasonType.Referral,
          response.payload.policy.number
        );
      }
      return unmarshalHomeQuoteResponse(response);
    }) // TODO: Create an unmarshalQuoteResponse for Retrieve, instead of using the Motor one
    .catch(() =>
      makeApiError(
        'Unable to retrieve your quote based on the details provided. Please try again or return to the Coles Insurance home page to do another quote.'
      )
    );
};

export const retrieveLandlordQuote = (
  quoteNumber: string,
  postcode: string,
  dob: string
): Promise<ApiData<LandlordQuoteDetails>> => {
  const server = connector(config.baseUrls.BFF_BASE_URL);

  const request = {
    brand: BRAND,
    product: WireProduct.Landlord,
    portfolio: WirePortfolio.ColesCp,
    channel: WireChannel.Web,
    version: REQUEST_VERSION,
    targetStatus: WireStatus.RetrieveQuote,
    policyNumber: toQuoteNumberWithDashes(quoteNumber),
    dob: toYYYYMMDD(dob),
    postcode,
  };
  return Promise.resolve()
    .then(() => server.post('/quote/landlord/retrieve', request))
    .then((response: WireHomeQuoteResponse) => {
      const {
        payload: { channel },
      } = response;
      if (channel && CLAIMS_CENTRE_CHANNELS.includes(channel)) {
        return makeBusinessError(
          ApiErrorReasonType.Referral,
          response.payload.policy.number
        );
      }
      return unmarshalLandlordQuoteResponse(response);
    }) // TODO: Create an unmarshalQuoteResponse for Retrieve, instead of using the Motor one
    .catch(() =>
      makeApiError(
        'Unable to retrieve your quote based on the details provided. Please try again or return to the Coles Insurance home page to do another quote.'
      )
    );
};

export default {
  retrieveMotorQuote,
  retrieveHomeQuote,
  retrieveLandlordQuote,
};
